import React from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchStats, fetchJobs } from "../services/api";

const GERMAN_WORDS = [
  "für", "und", "oder", "schwerpunkt", "ingenieur", "architekt",
  "automatisierung", "containertechnologien", "deutschkenntnisse",
  "stellenangebot", "stellenanzeig",
  "m/w/d", "(m/w/d)", ":innen", "*innen", "/innen", "willkommen",
  "entwickler", "berater", "leiter",
  "absolvent", "absolventin", "einstieg", "berufseinsteiger",
];

function detectLangNote(job) {
  if (job.langNote) return job.langNote;
  const t = (job.title || "").toLowerCase();
  const hasGerman = GERMAN_WORDS.some((w) => t.includes(w)) || /[äöüß]/.test(t);
  if (hasGerman) return "German likely";
  return "";
}

export default function StatsBar() {
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["stats"],
    queryFn: fetchStats,
  });

  const { data: jobs = [], isLoading: jobsLoading } = useQuery({
    queryKey: ["jobs"],
    queryFn: fetchJobs,
  });

  const isLoading = statsLoading || jobsLoading;

  const liveJobs = jobs.filter((j) => j.status === "new");
  const germanCount = liveJobs.filter((j) => {
    const lang = detectLangNote(j);
    return lang === "German required" || lang === "German likely";
  }).length;
  const germanPct = liveJobs.length > 0 ? Math.round((germanCount / liveJobs.length) * 100) : 0;

  const STATS = [
    {
      key: "total",
      label: "Total Scraped",
      value: stats?.total ?? 0,
      color: "text-brand-600 dark:text-brand-400",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
    },
    {
      key: "germanPct",
      label: "German Required",
      value: germanPct,
      suffix: "%",
      color: "text-orange-500 dark:text-orange-400",
      icon: <span className="text-sm">🇩🇪</span>,
    },
    {
      key: "applied",
      label: "Applied",
      value: stats?.applied ?? 0,
      color: "text-emerald-600 dark:text-emerald-400",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    {
      key: "interviews",
      label: "Interviews",
      value: stats?.interviews ?? 0,
      color: "text-purple-600 dark:text-purple-400",
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
    },
  ];

  return (
    <div className="space-y-3">
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {STATS.map((s) => (
        <div
          key={s.key}
          className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">
              {s.label}
            </span>
            <span className={`${s.color} opacity-70`}>{s.icon}</span>
          </div>
          <p className={`text-2xl font-bold ${s.color}`}>
            {isLoading ? (
              <span className="text-gray-300 dark:text-gray-700">—</span>
            ) : (
              `${s.value}${s.suffix ?? ""}`
            )}
          </p>
        </div>
      ))}
    </div>
    {stats && <CostMeter cost={stats.cost} />}
    </div>
  );
}

// This project must stay under $3/month of AWS usage (before credits). The
// figures come from the JobSeeker AWS Budget, which also emails at 50/80/100%
// and when the month is forecast to go over; AWS refreshes them a few times a day.
function CostMeter({ cost }) {
  if (!cost) {
    return (
      <div className="rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 px-4 py-2 text-xs text-gray-400 dark:text-gray-500">
        AWS cost: unavailable (budget could not be read)
      </div>
    );
  }
  const { actual, forecast, limit } = cost;
  const pct = Math.min(100, (actual / limit) * 100);
  const forecastOver = forecast != null && forecast > limit;
  const level = actual >= limit ? "over" : actual >= 0.8 * limit ? "high" : actual >= 0.5 * limit || forecastOver ? "warn" : "ok";
  const tone = {
    ok: { box: "border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900", bar: "bg-emerald-500", text: "text-gray-500 dark:text-gray-400" },
    warn: { box: "border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30", bar: "bg-amber-500", text: "text-amber-700 dark:text-amber-400" },
    high: { box: "border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30", bar: "bg-red-500", text: "text-red-700 dark:text-red-400" },
    over: { box: "border-red-300 dark:border-red-700 bg-red-100 dark:bg-red-950/50", bar: "bg-red-600", text: "text-red-800 dark:text-red-300 font-semibold" },
  }[level];
  const message = {
    ok: null,
    warn: forecastOver ? `Forecast $${forecast.toFixed(2)} is over the $${limit} cap` : "Half the monthly cap is used",
    high: "Over 80% of the monthly cap",
    over: "Monthly cap exceeded",
  }[level];

  return (
    <div className={`rounded-xl border px-4 py-2.5 ${tone.box}`}>
      <div className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs ${tone.text}`}>
        <span className="font-medium uppercase tracking-wider">AWS this month</span>
        <span>${actual.toFixed(2)} of ${limit.toFixed(2)}</span>
        {forecast != null && <span className="opacity-75">· forecast ${forecast.toFixed(2)}</span>}
        {message && <span className="ml-auto">⚠ {message}</span>}
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
        <div className={`h-full ${tone.bar}`} style={{ width: `${Math.max(pct, 1)}%` }} />
      </div>
    </div>
  );
}
